import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ICode } from '../../../models/codes.model';
import { doGetCodes } from '../../../store/code.slice';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import qs from 'qs';
import { useSnackbar } from 'notistack';
import { Select, Table, TableColumnsType } from 'antd';

import Loader from '../../../components/Loader';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Footer from '../../../components/layout/Footer';
import Button from '../../../components/Button';
import Checkbox from '../../../components/Checkbox';
import { errorVariant, warningVariant } from '../../../lib/barVariant';
import useAuth from '../../../hooks/useAuth';
const { Option } = Select;

const SystemCode: FC = () => {
  const { writeAuthYn } = useAuth();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { codes, isLoading } = useSelector((state: RootState) => state.code);
  const history = useHistory();
  const location = useLocation();
  let query = qs.parse(location.search, { ignoreQueryPrefix: true });
  const { enqueueSnackbar } = useSnackbar();

  const [codeData, setCodeData] = useState<ICode[]>([]);
  const [codeCount, setCodeCount] = useState(0);
  const [field, setField] = useState<any>();
  const [keyword, setKeyword] = useState('');
  const [isInUse, setIsInUse] = useState(false);

  const fieldList = [
    { label: t('system.code.column2'), value: 'korName' },
    { label: t('system.code.column3'), value: 'engName' }
  ];

  useEffect(() => {
    if (i18n.language === 'ko') {
      setField('korName');
    } else {
      setField('engName');
    }

    getCodes();
  }, []);

  useEffect(() => {
    // query to state
    if (query.field) {
      setField(String(query.field));
    }
    if (query.keyword) {
      setKeyword(String(query.keyword));
    }
    if (query.isInUse) {
      setIsInUse(query.isInUse === 'true');
    }

    // data filtering
    if (codes) {
      let data: ICode[] = codes;

      if (query.field && query.keyword) {
        data = data.filter(code => code[String(query.field)]?.toLowerCase()?.includes(String(query.keyword).toLowerCase()));
      }
      if (query.isInUse) {
        data = data.filter(code => code['useYn'] === 'Y');
      }

      setCodeCount(data.length);
      setCodeData(data);
    }
  }, [location, codes]);

  const getCodes = async () => {
    const res = await dispatch(doGetCodes());

    switch (res?.status) {
      case 200:
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      case 404:
        enqueueSnackbar(t('common.message.notFound'), warningVariant);
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  };

  const onSearch = () => {
    const param: any = {};

    if (field && keyword) {
      param.field = field;
      param.keyword = keyword;
    }
    if (isInUse) {
      param.isInUse = true;
    }

    history.push(`/system/code?${qs.stringify(param)}`);
  };

  const columns: TableColumnsType<ICode> = [
    {
      title: t('system.code.column1'),
      dataIndex: 'commonCode',
      key: 'commonCode',
      sorter: (a, b) => a.commonCode?.localeCompare(b.commonCode)
    },
    {
      title: t('system.code.column2'),
      dataIndex: 'korName',
      key: 'korName',
      sorter: (a, b) => a.korName?.localeCompare(b.korName)
    },
    {
      title: t('system.code.column3'),
      dataIndex: 'engName',
      key: 'engName',
      sorter: (a, b) => a.engName?.localeCompare(b.engName)
    },
    {
      title: t('system.code.column4'),
      dataIndex: 'useYn',
      key: 'useYn',
      sorter: (a, b) => a.useYn?.localeCompare(b.useYn)
    },
    {
      title: '',
      key: 'commonCode',
      dataIndex: 'commonCode',
      render: (value, record) => (
        <div className='comm-btn__area justify-content-center'>
          <Button
            className='btn-basic small pink-line'
            onClick={() => history.push(`/system/code/${record.commonCode}/edit${location.search}`)}
            text={t('common.button.edit')}
            writeAuthYn={writeAuthYn}
          />
          <Button
            className='btn-basic mint'
            onClick={() => {
              const param = {
                ko: record.korName,
                en: record.engName
              };
              history.push(`/system/code/${record.commonCode}/subcode${location.search ? `${location.search}&` : '?'}${qs.stringify(param)}`);
            }}
            text={t('system.code.button1')}
          />
        </div>
      ),
    }
  ];

  return (
    <>
    <SimpleHeader
      iconFile="icon-system-big.svg"
      parentMenu={t('common.menu.system')}
      currentMenu={t('system.code.title1')}
      onlyTitle={true}
    />

    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('system.code.column5')}</label>
            <div>
              <Select
                value={field}
                onChange={value => setField(value)}
              >
                {
                  fieldList?.map((item: any) => {
                    return (
                      <Option
                        key={item.value}
                        value={item.value}
                      >
                        {item.label}
                      </Option>
                    )
                  })
                }
              </Select>
            </div>
            <input
              type="text"
              className="form-control w-240p"
              value={keyword}
              onChange={(e => setKeyword(e.target.value))}
              onKeyPress={e => { if (e.key === 'Enter') onSearch() }}
            />
            <div className="comm-btn__area">
              <Button
                className='btn-basic small black'
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </li>
          <li>
            <label>{t('system.code.column4')}</label>
            <div className="text-left">
              <Checkbox
                checked={isInUse}
                onChange={setIsInUse}
                text={t('common.text.inUse')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>	

    <div className="content scrollbar">
      {isLoading ? <Loader /> : null}

      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">
            {t('common.text.count')}: <b>{codeCount}</b>{t('common.text.unit')}
          </div>
          <div className="comm-btn__area">
            <Button
              className='btn-basic pink'
              onClick={() => history.push(`/system/code/create${location.search}`)}
              text={t('common.button.create')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={codeData}
            pagination={{
              pageSize: 99999
            }}
          />
        </div>
        <div className="comm-bx__space">
          <div className="comm-btn__area right">
            <Button
              className='btn-basic pink'
              onClick={() => history.push(`/system/code/create${location.search}`)}
              text={t('common.button.create')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      </div>
      <Footer />
    </div>
    </>
  )
}

export default SystemCode;
